/**
 * The "Next Action" a PO line shows — who owes the next move.
 *
 * Lifted out of usePOLinesActions (CR-3410 / CR-4696) for Customer review
 * 11 Sep (review item 26b): the PO header's Lines panel showed the line's
 * goods-receipt status ("pending") where the reviewer wants the PO Lines grid's Next
 * Action. One pure function now serves both, so the two cannot drift apart.
 * The party itself comes from the serializer's `waiting_on`, which is derived
 * from acknowledge_status (the old portal's formatNextAction).
 */
import type { PurchaseOrderLine } from '../../lib/api';

export interface NextActionResult {
  text: string;
  className: string;
}

const WAITING_CUSTOMER: NextActionResult = { text: 'Waiting Customer', className: 'bg-blue-100 text-blue-700' };
const WAITING_SUPPLIER: NextActionResult = { text: 'Waiting Supplier', className: 'bg-orange-100 text-orange-700' };
const NO_ACTION: NextActionResult = { text: '-', className: 'text-muted-foreground' };

/** The side that owes the next move, with no rendering attached. */
export type NextActionParty = 'Customer' | 'Supplier';

/**
 * WHO owes the next move — the fact on its own, so every surface that names a
 * party names the same one.
 *
 * Walkthrough issue 4 (Aaron, 17 Sep 2026) is why this is separated out: the
 * grid read the hold and the line modal's header badge did not, so a line the
 * CUSTOMER had held read "Waiting Customer" in the grid and "Waiting on
 * Supplier" in the modal — on a line where the supplier can do nothing at all.
 * The modal had its own copy of the acknowledge_status mapping and never
 * learnt G7. The two renderings differ ("Waiting Supplier" in a narrow column,
 * "Waiting on Supplier" in the header); the ANSWER may not.
 *
 * @param line - The PO line.
 * @returns 'Customer', 'Supplier', or null when nobody owes a move.
 */
export function nextActionPartyFor(line: PurchaseOrderLine): NextActionParty | null {
  // CR-4696: Closed lines need no further action
  if (line.status === 'received') return null;
  if (line.on_hold) {
    // EXPEDITING-PARITY PR 2 (G7): the placer now ANSWERS whose move it is when
    // they place the hold, so stop guessing where they have told us. A customer
    // can hold a line waiting on the supplier, which is the case the guess got
    // wrong — Aaron: "shows its waiting on supplier when its actually on the
    // customer to take it off hold". Holds placed before the dialog asked carry
    // no nomination and fall back to the placer, which is what they always did.
    const owes = line.hold_next_action_with || line.on_hold_by_company_type;
    if (owes === 'customer' || owes === 'helix') return 'Customer';
    if (owes === 'supplier') return 'Supplier';
    return null;
  }
  if (line.status === 'cancelled') return null;
  if (line.waiting_on === 'Customer') return 'Customer';
  if (line.waiting_on === 'Supplier') return 'Supplier';
  return null;
}

/**
 * @param line - The PO line.
 * @returns The Next Action label and its badge classes; '-' when nobody owes a move.
 */
export function nextActionFor(line: PurchaseOrderLine): NextActionResult {
  const party = nextActionPartyFor(line);
  if (party === 'Customer') return WAITING_CUSTOMER;
  if (party === 'Supplier') return WAITING_SUPPLIER;
  // Held, but nobody recorded as owing it — a pre-G7 hold with no placer
  // either. Say so rather than showing a dash, which would read as "settled".
  if (line.on_hold && line.status !== 'received') {
    return { text: 'On Hold', className: 'bg-gray-100 text-gray-700' };
  }
  return NO_ACTION;
}
